import type { SwitcherProject } from '@vnetwork/margarita-shell/iam';

/** Most project rows the panel lists without a search (Design spec A10). */
export const SWITCHER_MAX = 10;
/** The search input appears when there are more projects than this (A10). */
const SEARCH_ABOVE = 8;

const ALL_PROJECTS = 'All projects';
export const SHARED_HEADING = 'Shared with you';

/** A project as the panel and the trigger show it. */
export interface ProjectView {
  project: SwitcherProject;
  /** In another organization than the home one: the row carries the Shared badge. */
  foreign: boolean;
  /** The organization's name: always set for a foreign project, for a home one only when it is known. */
  orgLabel?: string;
  /** Another project in a different organization has the same name (A6): trigger and search results add `orgLabel`. */
  ambiguous: boolean;
  /** First 6 characters of the id, when another project of the same organization has the same name (A7). */
  idHint?: string;
}

interface PanelGroup {
  /** The organization id. */
  key: string;
  foreign: boolean;
  heading: string;
  /** `Shared with you by <Organization name>` on a foreign heading (A3). */
  tooltip?: string;
  rows: ProjectView[];
}

interface PanelModel {
  /** The search input is shown (A10). */
  showSearch: boolean;
  /** Groups in display order; empty while a search is typed. */
  groups: PanelGroup[];
  /** The matches across all groups, flat; empty while no search is typed. */
  results: ProjectView[];
  /** Projects left out by the cap (`+N more`); 0 while searching. */
  more: number;
}

/** Lower-case and strip diacritics, as `Command` filters ("đ" → "d"). */
function fold(text: string): string {
  return text.trim().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/g, 'd');
}

function byName(a: { name: string; id: string }, b: { name: string; id: string }): number {
  return a.name.localeCompare(b.name, undefined, { sensitivity: 'base', numeric: true }) || a.id.localeCompare(b.id);
}

/** What a foreign organization is called when the shared list gave no name for it. */
function orgLabelOf(p: SwitcherProject): string {
  return p.orgName || `Organization ${p.organizationId}`;
}

/** The name of the home organization, from the entries of the shared list that are under it (the list holds own ones too). */
export function homeOrgNameOf(
  shared: readonly { customer_id: string; org_name: string }[],
  home: string | undefined,
): string | undefined {
  return (home && shared.find((p) => p.customer_id === home)?.org_name) || undefined;
}

/** Adds to every project what its row needs: foreign or not, the organization name, and how it differs from a namesake (A5-A7). */
export function viewProjects(projects: readonly SwitcherProject[], homeOrgName?: string): ProjectView[] {
  const orgsByName = new Map<string, Set<string>>();
  const countByNameInOrg = new Map<string, number>();
  for (const p of projects) {
    const n = fold(p.name);
    if (!orgsByName.has(n)) orgsByName.set(n, new Set());
    orgsByName.get(n)!.add(p.organizationId);
    const k = `${p.organizationId}\u0000${n}`;
    countByNameInOrg.set(k, (countByNameInOrg.get(k) ?? 0) + 1);
  }
  return projects.map((project) => {
    const n = fold(project.name);
    const foreign = project.orgName !== undefined;
    return {
      project,
      foreign,
      orgLabel: foreign ? orgLabelOf(project) : homeOrgName,
      ambiguous: (orgsByName.get(n)?.size ?? 0) > 1,
      idHint: (countByNameInOrg.get(`${project.organizationId}\u0000${n}`) ?? 0) > 1 ? project.id.slice(0, 6) : undefined,
    };
  });
}

/** Display order of the rows (A8): the home group, then the foreign ones A-Z by organization name; in a group the current project first, then A-Z. */
function orderGroups(views: readonly ProjectView[], currentId: string, hasForeign: boolean): PanelGroup[] {
  const byOrg = new Map<string, ProjectView[]>();
  for (const v of views) {
    const rows = byOrg.get(v.project.organizationId) ?? [];
    rows.push(v);
    byOrg.set(v.project.organizationId, rows);
  }
  const sorted = (rows: ProjectView[]) =>
    [...rows].sort((a, b) => {
      const ac = a.project.id === currentId ? 0 : 1;
      const bc = b.project.id === currentId ? 0 : 1;
      return ac - bc || byName(a.project, b.project);
    });
  const groups: PanelGroup[] = [];
  for (const [key, rows] of byOrg) {
    const foreign = rows[0].foreign;
    const org = rows[0].orgLabel ?? '';
    groups.push({
      key,
      foreign,
      heading: foreign ? org : hasForeign ? 'Your projects' : 'Working project',
      tooltip: foreign ? `Shared with you by ${org}` : undefined,
      rows: sorted(rows),
    });
  }
  return groups.sort((a, b) => {
    if (a.foreign !== b.foreign) return a.foreign ? 1 : -1;
    return a.foreign ? byName({ name: a.heading, id: a.key }, { name: b.heading, id: b.key }) : 0;
  });
}

/**
 * The first `max` rows in display order; the current project is always kept, in place of the last row, so the list
 * never hides the project the person is working in.
 */
function capRows(groups: PanelGroup[], currentId: string, max: number): PanelGroup[] {
  const ordered = groups.flatMap((g) => g.rows);
  if (ordered.length <= max) return groups;
  const keep = new Set(ordered.slice(0, max).map((v) => v.project.id));
  const current = ordered.find((v) => v.project.id === currentId);
  if (current && !keep.has(current.project.id)) {
    keep.delete(ordered[max - 1].project.id);
    keep.add(current.project.id);
  }
  return groups
    .map((g) => ({ ...g, rows: g.rows.filter((v) => keep.has(v.project.id)) }))
    .filter((g) => g.rows.length > 0);
}

/** Projects whose name or organization name contains the query, ignoring case and diacritics; all of them for an empty query. */
export function searchProjects(views: readonly ProjectView[], query: string): ProjectView[] {
  const q = fold(query);
  if (!q) return [...views];
  return views.filter((v) => fold(v.project.name).includes(q) || fold(v.orgLabel ?? '').includes(q));
}

/**
 * What the panel lists: groups capped at `SWITCHER_MAX` rows, or, while a search is typed, every project that matches
 * (the cap does not apply to a search, which covers all projects).
 */
export function panelModel(views: readonly ProjectView[], opts: { currentId: string; query: string }): PanelModel {
  const { currentId, query } = opts;
  const showSearch = views.length > SEARCH_ABOVE;
  if (fold(query)) {
    const results = searchProjects(views, query).sort((a, b) => {
      const ac = a.project.id === currentId ? 0 : 1;
      const bc = b.project.id === currentId ? 0 : 1;
      return ac - bc || byName(a.project, b.project);
    });
    return { showSearch, groups: [], results, more: 0 };
  }
  const hasForeign = views.some((v) => v.foreign);
  const groups = capRows(orderGroups(views, currentId, hasForeign), currentId, SWITCHER_MAX);
  return { showSearch, groups, results: [], more: Math.max(0, views.length - SWITCHER_MAX) };
}

/**
 * What choosing a project row stores, or null when it must not be stored yet: the home organization is unknown (an own
 * row has no organization), or the workspaces are not loaded and the row is a foreign one (the switch could not know
 * where to go).
 */
export function pickPayload(
  view: ProjectView,
  homeKnown: boolean,
  workspacesSettled = true,
): { projectId: string; organizationId: string } | null {
  const { id, organizationId } = view.project;
  if (view.foreign && !workspacesSettled) return null;
  return homeKnown && organizationId ? { projectId: id, organizationId } : null;
}

/** The trigger is `Loading…` while the project list loads, and while a stored choice is not yet checked against the lists. */
export function switcherLoading(args: { isLoading: boolean; stored: boolean; settled: boolean }): boolean {
  return args.isLoading || (args.stored && !args.settled);
}

/** `Open this project` is for an own project, and not while a foreign workspace is open. */
export function showOpenProject(args: { workingId: string; shownForeign: boolean; workspaceOrgName?: string }): boolean {
  return !!args.workingId && !args.shownForeign && !args.workspaceOrgName;
}

/** `+N more. Search or open the project list` (F). */
export function moreText(more: number): string {
  return `+${more} more. Search or open the project list`;
}

/** The sub-line under the shared placeholder when its list could not load (E3); a failed list has no organization to name. */
export function failedText(orgName?: string): string {
  return orgName ? `Couldn't load projects of ${orgName}.` : "Couldn't load shared projects.";
}

/** The `All projects` row (A9): inside a foreign workspace it is about that workspace's organization. */
export function allProjectsRow(workspaceOrgName?: string): { label: string; sub?: string } {
  return workspaceOrgName
    ? { label: `All projects in ${workspaceOrgName}` }
    : { label: ALL_PROJECTS, sub: 'Every project you can reach' };
}

/** What a project row says to a screen reader (A12): `<name>, Shared, <Organization name>`, plus `current`. */
export function rowSpoken(v: ProjectView, current: boolean): string {
  const name = v.idHint ? `${v.project.name} ${v.idHint}` : v.project.name;
  const parts = v.foreign ? [name, 'Shared', v.orgLabel ?? ''] : [name];
  if (current) parts.push('current');
  return parts.join(', ');
}

/** The text a project has in the trigger and in search results: `Default · <Organization name>` when it needs the qualifier (A6). */
function qualifiedName(v: ProjectView): string {
  const name = v.idHint ? `${v.project.name} ${v.idHint}` : v.project.name;
  return v.foreign || v.ambiguous ? (v.orgLabel ? `${name} · ${v.orgLabel}` : name) : name;
}

export interface TriggerLabel {
  kind: 'loading' | 'all' | 'home' | 'foreign' | 'organization';
  /** The main text: the project name, the organization name (kind `organization`), `All projects` or `Loading…`. */
  name: string;
  hint?: string;
  /** Muted `· <Organization name>` after the name. */
  org?: string;
  /** The Shared badge follows. */
  shared: boolean;
  /** The whole label in words: the trigger's `title` and `aria-label` (B1-B4). */
  spoken: string;
}

/**
 * The trigger label per state (B1-B5). `view` is the project being worked in; `workspaceOrgName` names the foreign
 * organization whose workspace is open, which wins when no project of it is chosen (B4) and when the chosen project
 * is not in it.
 */
export function triggerLabel(args: { view?: ProjectView; workspaceOrgName?: string; loading: boolean }): TriggerLabel {
  const { view, workspaceOrgName, loading } = args;
  if (view && (!workspaceOrgName || view.foreign)) {
    const hint = view.idHint;
    const name = view.project.name;
    if (view.foreign) {
      const org = view.orgLabel ?? '';
      return { kind: 'foreign', name, hint, org, shared: true, spoken: [hint ? `${name} ${hint}` : name, 'Shared', org].join(', ') };
    }
    const org = view.ambiguous ? view.orgLabel : undefined;
    return { kind: 'home', name, hint, org, shared: false, spoken: qualifiedName(view) };
  }
  if (workspaceOrgName) {
    return { kind: 'organization', name: workspaceOrgName, shared: true, spoken: `${workspaceOrgName}, Shared` };
  }
  if (loading) return { kind: 'loading', name: 'Loading…', shared: false, spoken: 'Loading…' };
  return { kind: 'all', name: ALL_PROJECTS, shared: false, spoken: ALL_PROJECTS };
}

/** The row at the top of the panel that leads back to the home workspace; only while a foreign workspace is open (C5). */
export const BACK_ROW_LABEL = 'Back to your organization';

/** The toast after a switch moved the page to another workspace (C1, C2). */
export function switchedText(label: string, toList: boolean): string {
  return toList ? `Switched to ${label}. Showing the list.` : `Switched to ${label}`;
}

/** The toast when the stored choice stopped being usable while its workspace was open (E4). */
export function noAccessText(label: string): string {
  return `You no longer have access to ${label}.`;
}

/** What a project is called in a toast: `<name> · <Organization name>` for a project of another organization. */
export function switchedLabel(view: ProjectView): string {
  return view.foreign && view.orgLabel ? `${view.project.name} · ${view.orgLabel}` : view.project.name;
}
